import { AffiliationRole, AffiliationStatus } from 'libs/affiliation/affiliation.model';

/**
 * Projection historique régénérée depuis les affiliations — ce que les
 * anciens clients mobiles lisent/écrivent encore (`users/{id}.role`,
 * `.trainings`). Fonction pure, testable sans Firestore.
 *
 * Voir rapport-link-up/PLAN_SPRINTS_ET_AUDIT_ROLES.md, section Sprint 2,
 * "Compatibilité avec les anciens clients mobiles".
 */

export type ProjectableAffiliation = {
  trainingId: string;
  role: AffiliationRole;
  status: AffiliationStatus;
};

export interface LegacyRoleProjection {
  role: string | undefined;
  trainings: string[];
}

/**
 * `trainings` : union de toutes les formations de l'identité, tout rôle et
 * statut confondus — un historique de formation archivée n'est jamais purgé.
 *
 * `role` : premier qui correspond dans cet ordre de priorité.
 *
 * Tuteur et coordonnateur comptent quel que soit le statut de l'affiliation :
 * contrairement à l'apprenti, l'ancien modèle n'a jamais eu de notion d'« ex-
 * tuteur » ou d'« ex-coordonnateur » — `archive-training.component.ts` ne
 * bascule que `apprentice.role = 'alumni'` à l'archivage, jamais le rôle d'un
 * tuteur ou d'un coordonnateur. Exiger un statut actif pour eux aurait projeté
 * `undefined` pour toute personne dont la seule formation tutée est archivée,
 * alors que sa fiche reste `role: 'tutor'` aujourd'hui — confirmé en
 * confrontant cet algorithme aux données réelles de `craf2s-dev` (68 écarts
 * avant correction, 0 après).
 *
 * apprentice-actif est prioritaire sur tuteur/coordonnateur-tout-statut : le
 * S2-03 garantit qu'il n'y en a jamais qu'un, donc aucun conflit avec le
 * comptage de quota (`training-center.service.ts`, qui sort tôt si
 * `role !== 'apprentice'`).
 */
const PRIORITE: { predicate: (a: ProjectableAffiliation) => boolean; legacyRole: string }[] = [
  { predicate: (a) => a.role === 'apprentice' && a.status === 'active', legacyRole: 'apprentice' },
  { predicate: (a) => a.role === 'tutor', legacyRole: 'tutor' },
  { predicate: (a) => a.role === 'manager', legacyRole: 'manager' },
  { predicate: (a) => a.role === 'apprentice' && a.status === 'ended', legacyRole: 'alumni' },
];

/**
 * Prend l'ensemble des affiliations de l'identite, actives ET terminees : la
 * detection alumni/tuteur/coordonnateur ne peut matcher que sur une
 * affiliation terminee, que getActiveAffiliations() exclurait.
 */
export function projectLegacyRole(affiliations: ProjectableAffiliation[]): LegacyRoleProjection {
  const trainings = Array.from(new Set((affiliations ?? []).map((a) => a.trainingId)));

  for (const { predicate, legacyRole } of PRIORITE) {
    if (affiliations?.some(predicate)) {
      return { role: legacyRole, trainings };
    }
  }

  return { role: undefined, trainings };
}

/**
 * Sélecteur de contexte (Sprint 3) — même règles que `projectLegacyRole`,
 * mais retourne TOUS les rôles distincts tenus plutôt que le seul
 * prioritaire : une identité tuteur ET stagiaire doit se voir proposer les
 * deux, pas seulement celui qui gagnerait la projection historique.
 */
export function projectAvailableContexts(affiliations: ProjectableAffiliation[]): string[] {
  return PRIORITE.filter(({ predicate }) => affiliations?.some(predicate)).map(({ legacyRole }) => legacyRole);
}
